import React, { useEffect, useRef, useState } from 'react';
import {
  Bot,
  Send,
  Sparkles,
  User,
  Loader2,
  Wand2,
  ArrowRight,
  Zap,
  Check,
  Code2,
} from 'lucide-react';
import type { Theme, ThemeColors, ReportEntity } from './types';
import type { TemplateParts } from './templateParts';
import {
  COPILOT_STARTER_PROMPTS,
  generateFromPrompt,
  type CopilotGenerationResult,
} from './reportCopilot';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  generation?: CopilotGenerationResult;
}

export interface ReportCopilotPanelProps {
  theme?: Theme;
  onApply: (
    result: {
      parts: TemplateParts;
      entities: ReportEntity[];
      suggestedName?: string;
    },
    options?: { navigateToSource?: boolean }
  ) => void;
  onPreviewDraft: (result: { parts: TemplateParts; entities: ReportEntity[] }) => void;
  onSwitchToSource: () => void;
}

const ReportCopilotPanel: React.FC<ReportCopilotPanelProps> = ({
  theme = 'dark',
  onApply,
  onPreviewDraft,
  onSwitchToSource,
}) => {
  const colors = THEME_CONFIG[theme];
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [autoApply, setAutoApply] = useState(true);
  const [autoAppliedIds, setAutoAppliedIds] = useState<Set<string>>(new Set());
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        "Hi — I'm your Analytics Copilot. Describe the report you need and I'll generate a starter layout with sample entities and bindings. Try one of the prompts below or describe your own.",
    },
  ]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isGenerating]);

  const runGeneration = async (prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed || isGenerating) return;

    setInput('');
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: trimmed },
    ]);
    setIsGenerating(true);

    await new Promise((r) => window.setTimeout(r, 900 + Math.random() * 600));

    const result = generateFromPrompt(trimmed);
    const messageId = `a-${Date.now()}`;
    onPreviewDraft({ parts: result.parts, entities: result.entities });

    if (autoApply) {
      onApply(
        {
          parts: result.parts,
          entities: result.entities,
          suggestedName: result.suggestedName,
        },
        { navigateToSource: false }
      );
      setAutoAppliedIds((prev) => new Set(prev).add(messageId));
    }

    setMessages((prev) => [
      ...prev,
      {
        id: messageId,
        role: 'assistant',
        content: autoApply
          ? `${result.summary.replace(/\*\*/g, '')} Applied to your report automatically.`
          : result.summary.replace(/\*\*/g, ''),
        generation: result,
      },
    ]);
    setIsGenerating(false);
  };

  const handleApply = (generation: CopilotGenerationResult, messageId: string) => {
    onApply(
      {
        parts: generation.parts,
        entities: generation.entities,
        suggestedName: generation.suggestedName,
      },
      { navigateToSource: true }
    );
    setAutoAppliedIds((prev) => new Set(prev).add(messageId));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void runGeneration(input);
    }
  };

  return (
    <div className={`flex flex-col h-full min-h-0 ${colors.background}`}>
      <div className={`flex-shrink-0 px-4 py-3 border-b ${colors.border} ${colors.primary}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 flex-shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className={`text-xs font-semibold ${colors.text.primary} flex items-center gap-1.5`}>
                Analytics Copilot
                <Sparkles className="w-3 h-3 text-violet-400" />
              </h3>
              <p className={`text-[10px] ${colors.text.muted} truncate`}>
                Describe, generate, preview — refine in Source when ready
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAutoApply((v) => !v)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-medium transition-colors ${
              autoApply
                ? 'border-violet-500/40 bg-violet-500/15 text-violet-300'
                : `${colors.border} ${colors.secondary} ${colors.text.muted} ${colors.hover}`
            }`}
            title="Automatically apply generated layouts to the report"
          >
            <Zap className={`w-3 h-3 ${autoApply ? 'text-violet-400' : ''}`} />
            Auto apply
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                msg.role === 'assistant'
                  ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
                  : `${colors.secondary} border ${colors.border}`
              }`}
            >
              {msg.role === 'assistant' ? (
                <Bot className="w-3.5 h-3.5 text-white" />
              ) : (
                <User className={`w-3.5 h-3.5 ${colors.text.muted}`} />
              )}
            </div>
            <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
              <div
                className={`inline-block px-3 py-2 rounded-xl text-[11px] leading-relaxed text-left ${
                  msg.role === 'assistant'
                    ? `${colors.surface} border ${colors.border} ${colors.text.secondary}`
                    : 'bg-indigo-600 text-white'
                }`}
              >
                {msg.content}
              </div>
              {msg.generation && (
                <div
                  className={`mt-2 p-3 rounded-xl border ${colors.border} ${colors.surface} text-left`}
                >
                  <p className={`text-[10px] font-medium ${colors.text.primary} mb-1`}>
                    {msg.generation.template.name}
                  </p>
                  <p className={`text-[10px] ${colors.text.muted} mb-2.5 line-clamp-2`}>
                    {msg.generation.template.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${colors.secondary} ${colors.text.muted}`}>
                      {msg.generation.entities.length} entities
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${colors.secondary} ${colors.text.muted}`}>
                      {msg.generation.template.category}
                    </span>
                    {autoAppliedIds.has(msg.id) && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-0.5">
                        <Check className="w-2.5 h-2.5" />
                        Applied
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {!autoApply && !autoAppliedIds.has(msg.id) && (
                      <button
                        type="button"
                        onClick={() => handleApply(msg.generation!, msg.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-medium transition-colors"
                      >
                        <Wand2 className="w-3 h-3" />
                        Apply to report
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                    {autoAppliedIds.has(msg.id) && (
                      <button
                        type="button"
                        onClick={() => onSwitchToSource()}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${colors.border} ${colors.hover} ${colors.text.secondary} text-[10px] font-medium transition-colors`}
                      >
                        <Code2 className="w-3 h-3 text-indigo-400" />
                        Edit in Source
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {isGenerating && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            <div className={`px-3 py-2 rounded-xl border ${colors.border} ${colors.surface}`}>
              <div className={`flex items-center gap-2 text-[11px] ${colors.text.muted}`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                Generating sample layout…
              </div>
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && !isGenerating && (
        <div className={`flex-shrink-0 px-4 pb-2 flex flex-wrap gap-1.5`}>
          {COPILOT_STARTER_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => void runGeneration(prompt)}
              className={`text-[10px] px-2.5 py-1.5 rounded-full border ${colors.border} ${colors.secondary} ${colors.text.muted} ${colors.hover} transition-colors text-left`}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      <div className={`flex-shrink-0 p-3 border-t ${colors.border} ${colors.primary}`}>
        <div className={`flex gap-2 items-end rounded-xl border ${colors.border} ${colors.secondary} p-2`}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the analytics report you want to build…"
            rows={2}
            className={`flex-1 resize-none bg-transparent text-[11px] outline-none ${colors.text.primary} placeholder-neutral-500`}
            disabled={isGenerating}
          />
          <button
            type="button"
            onClick={() => void runGeneration(input)}
            disabled={!input.trim() || isGenerating}
            className="flex-shrink-0 p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
            title="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className={`text-[9px] ${colors.text.muted} mt-1.5 px-1`}>
          Enter to send · Shift+Enter for new line · {autoApply ? 'Auto apply on' : 'Manual apply'}
        </p>
      </div>
    </div>
  );
};

export default ReportCopilotPanel;
